/**
 * The bottom tab bar host a PageShell offers a `variant: "tabbar"` ModeSwitch.
 */
import { createContext } from 'react';
import { evalWhen } from '../paths';
import { resolveProps } from '../props';

/** `options` (the segmented word) and `tabs` (the pill one) are one list. */
export function optionsOf(props) {
  const list = props.options !== undefined ? props.options : props.tabs;
  return Array.isArray(list) ? list.filter(Boolean) : [];
}

/**
 * The PageShell that pinned a `tabbar` ModeSwitch: `{ tabbarId, target, scroller }`.
 * `target` is the DOM slot under the shell body the bar is portaled into.
 */
export const TabBarHostContext = createContext(null);

/** A `tabbar` draws as a bottom bar only with 2–5 options. */
export function tabBarFits(count) {
  return count >= 2 && count <= 5;
}

/**
 * The first `variant: "tabbar"` ModeSwitch under a shell's body items, in
 * document order, skipping `when`-false subtrees, `repeat` nodes and other
 * ModeSwitch bodies — the Flutter shell walks the same tree the same way.
 */
export function findTabBarNode(items, components, ctx) {
  for (const { id, node } of items) {
    if (!node || node.repeat != null) continue;
    if (node.when !== undefined && !evalWhen(node.when, ctx)) continue;
    if (node.type === 'ModeSwitch') {
      const props = resolveProps(node.props || {}, { dataProps: ['options'] }, ctx);
      if ((props.variant || node.variant) === 'tabbar' && tabBarFits(optionsOf(props).length)) return id;
      continue;
    }
    const kids = (node.children || []).map((child, i) => ({
      id: typeof child === 'string' ? child : `${id}__child${i}`,
      node: typeof child === 'string' ? components?.[child] : child,
    }));
    const found = findTabBarNode(kids, components, ctx);
    if (found) return found;
  }
  return null;
}

/** A tab's count badge text: `99+` past 99, null when 0, absent or non-numeric. */
export function tabCountLabel(value) {
  const n = typeof value === 'number' ? value : (typeof value === 'string' && value.trim() ? Number(value) : NaN);
  if (!Number.isFinite(n) || n < 1) return null;
  const whole = Math.floor(n);
  return whole > 99 ? '99+' : String(whole);
}
